import {useMutation, useQuery} from '@tanstack/react-query';
import {
  IApiErrorResponse,
  IGetMySearchesTreeDetailsResponse,
  IMoveSavedSearchesPayload,
  IMoveSavedSearchesResponse,
  ISavedSearchDataResponse,
  ISavedSearchDeletePayload,
  ISavedSearchDeleteResponse,
  ISavedSearchPayload,
  ISavedSearchReanamePayload,
  ISavedSearchRenameResponse,
  ISavedSearchSharePayload,
  ISavedSearchShareResponse,
  IShareSearchGroupsResponse,
} from '../../api/types/savedsearch.types';
import {
  batchUpload,
  deleteSearch,
  getMySearchesTreeDetails,
  downloadTemplate,
  getSavedSearchList,
  getShareSearchGroups,
  moveSavedSearches,
  renameSearch,
  shareSearch,
  getSavedSearchTreeData,
} from '../../api/endpoints/savedSearchApi';

const SAVED_SEARCH_PROGRESS_POLL_INTERVAL_MS = 10_000;

export const useSavedSearchList = (
  payload: ISavedSearchPayload,
  enabled: boolean,
  pollWhileInProgress = false
) => {
  return useQuery<ISavedSearchDataResponse, Error>({
    queryKey: ['reportLanding', payload],
    queryFn: () => getSavedSearchList(payload),
    enabled,
    refetchInterval: (query) => {
      if (!pollWhileInProgress) return false;

      return query.state.data?.data[0]?.status === 'INPROGRESS'
        ? SAVED_SEARCH_PROGRESS_POLL_INTERVAL_MS
        : false;
    },
    refetchIntervalInBackground: pollWhileInProgress,
  });
};

export const useGetShareGroups = (call: boolean) => {
  return useQuery<IShareSearchGroupsResponse, Error>({
    queryKey: ['shareGroups'],
    queryFn: () => getShareSearchGroups(),
    enabled: call,
  });
};

export const useShareSavedSearch = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ISavedSearchShareResponse,
    Error,
    ISavedSearchSharePayload
  >({
    mutationFn: shareSearch,
    mutationKey: ['shareSearch'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useRenameSavedSearch = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ISavedSearchRenameResponse,
    Error,
    ISavedSearchReanamePayload
  >({
    mutationFn: renameSearch,
    mutationKey: ['renameSearch'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDeleteSavedSearch = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ISavedSearchDeleteResponse,
    Error,
    ISavedSearchDeletePayload
  >({
    mutationFn: deleteSearch,
    mutationKey: ['deleteSearch'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetMySearchesTreeDetails = () => {
  return useQuery<IGetMySearchesTreeDetailsResponse, IApiErrorResponse>({
    queryKey: ['mySearchesTreeDetails'],
    queryFn: () => getMySearchesTreeDetails(),
  });
};

export const useMoveSavedSearches = (
  onSuccess?: () => void,
  onError?: (err: IApiErrorResponse) => void
) => {
  return useMutation<
    IMoveSavedSearchesResponse,
    IApiErrorResponse,
    IMoveSavedSearchesPayload
  >({
    mutationFn: moveSavedSearches,
    mutationKey: ['moveSavedSearches'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDownloadTemplate = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: downloadTemplate,
    mutationKey: ['downloadTemplate'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useBatchUploadSavedSearch = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: batchUpload,
    mutationKey: ['batchUpload'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetSavedSearchTreeData = () => {
  return useQuery({
    queryKey: ['search.saved.treeData.all'],
    queryFn: () => getSavedSearchTreeData(),
  });
};
